<template>
  <Dialog
    v-model="useDialogStore().addonCreate.isOpen"
    size="small-hug"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('addon.create.title')"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-80 w-full"
    />
    <div v-else>
      <Form
        ref="formRef"
        :model="form"
        :rules="rules"
        @submit="onSubmit"
      >
        <InputBlock
          :label="useT('addon.create.type')"
          prop="type"
          direction="horizontal"
        >
          <Select
            :model-value="'ai'"
            :options="[{ id: 'ai', text: useT('addon.type.ai') }]"
            disabled
          />
        </InputBlock>

        <InputBlock
          :label="useT('addon.create.period')"
          prop="refillPeriod"
          direction="horizontal"
        >
          <RadioGroup
            :model-value="form.refillPeriod"
            :options="[
              { id: 'oneTime', text: useT('addon.period.oneTime') },
              { id: 'monthly', text: useT('addon.period.monthly') },
            ]"
            @update:model-value="(value) => _set(form, 'refillPeriod', value)"
          />
          <div class="mt-1 text-xs text-gray-500">
            {{ useT(form.refillPeriod === 'monthly' ? 'addon.create.monthlyHint' : 'addon.create.oneTimeHint') }}
          </div>
        </InputBlock>

        <InputBlock
          :label="useT('addon.create.tier')"
          prop="tier"
          direction="horizontal"
        >
          <Select
            :model-value="form.tier"
            :options="tierOptions"
            @update:model-value="(value) => _set(form, 'tier', value)"
          />
        </InputBlock>

        <InputBlock
          v-if="form.tier === 'custom'"
          :label="useT('addon.create.customCredits')"
          prop="customCredits"
          direction="horizontal"
        >
          <NumberInput
            :model-value="form.customCredits"
            class="w-full"
            :min="1"
            @update:model-value="(value) => _set(form, 'customCredits', value)"
          />
        </InputBlock>

        <!-- A monthly add-on refills on its monthly dates until this end date and
             on none after it. Prefilled a year out; renewals are tracked in the
             CRM. -->
        <InputBlock
          v-if="form.refillPeriod === 'monthly'"
          :label="useT('addon.expiringAt')"
          prop="expiringAt"
          direction="horizontal"
        >
          <DatePicker
            v-model="form.expiringAt"
            class="w-full"
          />
        </InputBlock>
      </Form>
    </div>

    <template
      v-if="!isLoading"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('addon.create.submit')"
        :text-cancel="useT('bcd205')"
        align="left"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('addonCreate')"
      />
    </template>
  </Dialog>
</template>

<script setup>
const tiers = [
  { id: 'start', label: 'Start', credits: 50 },
  { id: 'plus', label: 'Plus', credits: 150 },
  { id: 'max', label: 'Max', credits: 500 },
];

const tierOptions = computed(() => [
  ...tiers.map((tier) => ({ id: tier.id, text: `${tier.label} - ${useT('addon.xCredits', { value: tier.credits })}` })),
  { id: 'custom', text: useT('addon.tier.custom') },
]);

const defaultForm = () => ({
  tier: 'start',
  customCredits: 100,
  refillPeriod: 'oneTime',
  expiringAt: dayjs().add(1, 'year').toDate(),
});

const form = ref(defaultForm());

const rules = {};

const isLoading = ref(false);
const isSubmitting = ref(false);
const formRef = ref(null);

const onOpen = () => {
  form.value = defaultForm();
};

const onClose = () => {
  form.value = defaultForm();
};

const resolveCredits = () => {
  if (form.value.tier === 'custom') return Number(form.value.customCredits) || 0;
  return tiers.find((entry) => entry.id === form.value.tier)?.credits || 0;
};

const onSubmit = async() => {
  const credits = resolveCredits();
  if (!credits || credits < 1) return;

  isSubmitting.value = true;

  const isMonthly = form.value.refillPeriod === 'monthly';
  const response = await useAddonsStore().create({
    name: 'ai',
    workspace: useDialogStore().addonCreate.workspaceDomain,
    data: {
      refillPeriod: isMonthly ? 'monthly' : null,
      credits,
    },
    expiringAt: isMonthly ? (form.value.expiringAt || null) : null,
  });

  isSubmitting.value = false;

  if (response) {
    useToast(useT('bcd138'));
    const onSubmitCallback = useDialogStore().addonCreate.onSubmit;
    useDialogStore().close('addonCreate');
    if (typeof onSubmitCallback === 'function') {
      onSubmitCallback(response);
    }
  }
};
</script>
